import { GridSortItem } from "@mui/x-data-grid";
import { useState } from "react";
import CustomDataGridV2 from "../../../../../components/CustomDataGridV2";
import Text from "../../../../../components/Text";
import ActionMenu from "../../../../../components/app/ActionMenu";
import DeleteModal from "../../../../../components/app/DeleteModal";
import useDisclosure from "../../../../../hooks/use-disclosure";
import { useArchiveState } from "../../../../../hooks/useArchiveState";
import { useSnackbar } from "../../../../../hooks/useSnackbar";
import { useArchivePatientAssessment } from "../../../../../queries/patient-chart/assessment/assement-forms/use-archive-patient-assessment";
import { useGetAllPatientAssessment } from "../../../../../queries/patient-chart/assessment/assement-forms/use-get-all-patient-assessment";
import { useSendPatientAssessments } from "../../../../../queries/patient-chart/assessment/assement-forms/use-send-patient-assessments";
import { formatDateTime } from "../../../../../utils/date-time-utility";
import { PatientName } from "../../../../../utils/util-components/UtilComponents";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  applyDefaultColumnSettings,
  getXtenantId,
} from "../../../../../utils/utils";
import { AssessmentFilterData } from "./AssessmentsList";
import StyledButtonV2 from "../../../../../components/app/StyledButtonV2";
import { BILLING_TABLE_ROW_HEIGHT } from "../../../../../components/app/provider/patient/chart";
import PageLayout from "../../../../../components/PageLayout";
import SendPatientAssessmentFormsModal from "../../patient/patient-list/patient-chart-new/assessment/PatientAssementForms/SendPatientAssementFormsModal";

interface AssessmentSendScheduledProps {
  filters: AssessmentFilterData;
  searchKey: string;
  onAssessmentsChange?: (assessments: any[]) => void;
  scheduledStatus: "sent" | "scheduled"; // Controls which API call to make
  archive: boolean;
}

/**
 * Extracts the short name from assessment type label.
 * Example: "AAQ II (Acceptance And Action Questionnaire-II)" -> "AAQ II"
 */
const extractAssessmentTypeShortName = (
  label: string | undefined
): string | undefined => {
  if (!label) return label;
  // Extract "AAQ II" from "AAQ II (Acceptance And Action Questionnaire-II)"
  const regex = /^([^(]+)/;
  const match = regex.exec(label);
  return match ? match[1].trim() : label;
};

const AssessmentSendScheduled = ({
  filters,
  searchKey,
  onAssessmentsChange,
  scheduledStatus,
  archive,
}: AssessmentSendScheduledProps) => {
  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);
  const [gridSorting, setGridSorting] = useState<GridSortItem[]>([]);
  const { mutateAsync: sendPatientForms } = useSendPatientAssessments();
  const showSnackbar = useSnackbar();
  // Use scheduledStatus to determine the scheduled parameter
  const isScheduled = scheduledStatus === "scheduled";

  const {
    data: assessmentsList,
    isPending,
    refetch,
  } = useGetAllPatientAssessment({
    pagination: pagination,
    xTENANTID: getXtenantId(),
    scheduled: isScheduled, // false for sent, true for scheduled
    sendDate: filters.send_date,
    submittedDate: filters.submitted_date,
    archive: isScheduled ? archive : false,
    searchString: searchKey,
    sendBy: (filters.provider_Name as any)?.label,
    screenerType: extractAssessmentTypeShortName(
      (filters.assessment_type as any)?.label
    ),
    sortDirection: "desc",
  });

  // Notify parent of assessments change
  if (assessmentsList?.data?.content && onAssessmentsChange) {
    onAssessmentsChange(assessmentsList.data.content);
  }

  const handleResend = (row: any) => {
    const payload = {
      patientAssessments: {
        assessment: [row.name],
        reminderMedium: ["EMAIL"],
        isResend: true,
      },
      patientIds: [row.patientId],
    };

    sendPatientForms(payload as any)?.then(() => {
      showSnackbar({
        type: "success",
        message: "Assessment resend successfully",
      });
      refetch();
    });
  };

  const { mutateAsync: archiveMutation } = useArchivePatientAssessment();
  const { deleteDisc, archiveHandler } = useArchiveState(archiveMutation);
  const [archiveAssessment, setArchiveAssessment] = useState<boolean>(false);

  const editAssessmentDisc = useDisclosure();

  const COLUMNS = applyDefaultColumnSettings<any>([
    {
      field: "srNo",
      headerName: "Sr. No",
      width: 70,
      renderCell: (params: any) =>
        params.api.getAllRowIds().indexOf(params.id) + 1,
    },
    {
      field: "patientName",
      flex: 0.3,
      minWidth: 150,
      disableColumnMenu: true,
      headerName: "Patient Name",
      renderCell: ({ row }: any) => (
        <PatientName
          name={row?.patientName}
          gender={row?.patientGender}
          patientId={row?.patientId}
          birthDate={row?.patientDob}
        />
      ),
    },
    {
      field: "name",
      flex: 0.5,
      minWidth: 130,
      disableColumnMenu: true,
      headerName: "Type",
      renderCell: ({ value }: any) => <Text isTabletext>{value || "-"}</Text>,
    },
    {
      field: "sendBy",
      headerName: isScheduled ? "Scheduled by" : "Sent by",
      flex: 0.5,
      width: 150,
      renderCell: ({ row }: any) => (
        <Text isTabletext>
          {row?.sendBy || row?.providerName
            ? `${row?.sendBy || row?.providerName}${
                row?.providerSpecialty ? `, ${row.providerSpecialty}` : ""
              }`
            : "-"}
        </Text>
      ),
    },
    {
      field: "created",
      headerName: isScheduled ? "Scheduled on" : "Sent on",
      flex: 0.5,
      width: 160,
      renderCell: ({ row }: any) => (
        <Text isTabletext>
          {isScheduled
            ? row?.sendOn
              ? formatDateTime(row?.sendOn)
              : "-"
            : row?.created
              ? formatDateTime(row?.created)
              : "-"}
        </Text>
      ),
    },
    // ...(isScheduled
    //   ? [
    //       {
    //         field: "status",
    //         headerName: "Status",
    //         flex: 0.5,
    //         width: 160,
    //         renderCell: () => (
    //           <Chip
    //             size="small"
    //             variant="outlined"
    //             label={<Text>Scheduled</Text>}
    //           />
    //         ),
    //       } as any,
    //     ]
    //   : []),
    {
      field: "Action",
      headerName: "Action",
      width: isScheduled ? 90 : 120,
      sortable: false,
      renderCell: ({ row }: any) => {
        if (isScheduled) {
          return (
            <ActionMenu
              options={[
                {
                  value: "Edit",
                  label: "Edit",
                  isDisabled: false,
                  hoverMsg: "Edit assessment",
                },
                {
                  value: "archive",
                  label: row?.archive ? "Unarchive" : "Archive",
                  isDisabled: false,
                  hoverMsg: "Make status disable for archive",
                },
              ]}
              onActionClick={(v) => {
                if (v === "Edit") {
                  editAssessmentDisc.open({
                    editAssessment: {
                      assessmentId: row?.uuid,
                      patientId: row?.patientId,
                      patientName: row?.patientName,
                      appointmentId: row?.appointmentId ?? null,
                      screenerType: row?.name,
                      scheduleTime: row?.sendOn,
                    },
                  });
                } else if (v === "archive") {
                  setArchiveAssessment(row?.archive);
                  deleteDisc.open(row?.uuid);
                }
              }}
            />
          );
        }
        return (
          <StyledButtonV2
            onClick={() => handleResend(row)}
            variant="text"
            label="Resend"
          />
        );
      },
    },
  ] as any);

  return (
    <PageLayout>
      <DeleteModal
        w="380px"
        open={deleteDisc.opened}
        onClose={deleteDisc.close}
        id={deleteDisc.deleteId}
        onConfirmation={(id) =>
          archiveHandler(!archiveAssessment, id).then((res) => {
            refetch();
            return res;
          })
        }
        content={`You want to ${
          archiveAssessment ? "unarchive" : "archive"
        } this assessment`}
      />
      {editAssessmentDisc.opened && (
        <SendPatientAssessmentFormsModal
          disclosureHost={editAssessmentDisc}
          editMode
          onUpdateSuccess={() => refetch()}
        />
      )}
      <CustomDataGridV2
        getRowId={(row) => row?.uuid}
        loading={isPending}
        rows={assessmentsList?.data?.content ?? []}
        rowCount={assessmentsList?.data?.page?.totalElements ?? 10}
        columns={COLUMNS}
        paginationModel={pagination}
        onPaginationModelChange={setPagination}
        onSortModelChange={setGridSorting}
        sortModel={gridSorting}
        rowHeight={BILLING_TABLE_ROW_HEIGHT}
      />
    </PageLayout>
  );
};

export default AssessmentSendScheduled;
